import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Row, Col, Card, Table, Badge, Button, OverlayTrigger, Tooltip, Form, InputGroup, Modal, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification, NotificationMessages } from '../hooks/useNotification';
import ProjectEditModal from './ProjectEditModal';
import ProjectProposalModal from './ProjectProposalModal';
import { getStatusVariant, getStatusDisplayText } from '../utils/statusUtils';
import { handleApiResponse, API_ENDPOINTS, apiClient } from '../utils/apiUtils';
import { formatDateTime } from '../utils/uiUtils';
import { canEditItem, canDeleteItem } from '../utils/permissionUtils';
import './Projects.css';

// Comprehensive list of project statuses for filtering (independent of loaded data)
const ALL_PROJECT_STATUSES = [
  'Pending',
  'Reviewed by GAD',
  'Approved by Extension Head',
  'Rejected by Extension Head',
  'Approved by VC-RDES',
  'Rejected by VC-RDES',
  'Approved by VC-AF',
  'Rejected by VC-AF',
  'Approved by Chancellor',
  'Rejected by Chancellor',
  'Rejected by Admin',
  'On-Going',
  'Completed'
];

// Status variant function moved to utils/statusUtils.js

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();
  const { user } = useAuth();

  // Any authenticated user can propose a project
  const canPropose = !!user;
  const location = useLocation();
  const successMessage = location.state?.success;

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    
    try {
      const response = await handleApiResponse(
        () => axios.delete(`${API_ENDPOINTS.PROJECTS}/${projectToDelete.project_id}`),
        'delete project'
      );
      
      if (response.success) {
        setProjects(prev => prev.filter(p => p.project_id !== projectToDelete.project_id));
        setFilteredProjects(prev => prev.filter(p => p.project_id !== projectToDelete.project_id));
        showSuccess(NotificationMessages.PROJECT_DELETED);
      } else {
        showError(response.error.message);
      }
    } catch (e) {
      console.error('Delete project failed', e);
      showError(NotificationMessages.PROJECT_DELETE_FAILED);
    } finally {
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  // Handle opening edit modal
  const handleEditProject = (project) => {
    setProjectToEdit(project);
    setShowEditModal(true);
  };

  // Get statuses for filter dropdown (show all possible, not just present in list)
  const getUniqueStatuses = () => {
    return ['All', ...ALL_PROJECT_STATUSES];
  };

  // Filter projects based on search term and status
  useEffect(() => {
    let filtered = projects;

    // Filter by search term (title or coordinator name)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(project => 
        project.title.toLowerCase().includes(term) ||
        (project.coordinator_fullname && project.coordinator_fullname.toLowerCase().includes(term))
      );
    }

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    setFilteredProjects(filtered);
  }, [projects, searchTerm, statusFilter]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data } = await apiClient.get('/projects');
        console.log('Fetched projects:', data);
        console.log('Current user:', user);
        setProjects(data);
      } catch (err) {
        console.error('Failed to load projects', err);
        showError('Failed to load projects. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [user, showError]);

  // Show success message from navigation if exists
  useEffect(() => {
    if (successMessage) {
      showSuccess(successMessage);
    }
  }, [successMessage, showSuccess]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
  };

  const hasActiveFilters = searchTerm.trim() || statusFilter !== 'All';

  return (
    <div className="projects-container">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Projects</h3>
            {canPropose && (
              <Button 
                variant="success"
                onClick={() => setShowProposalModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Propose Project
              </Button>
            )}
          </div>

          {/* Search and Filter Section */}
          <Card className="shadow-sm mb-3 search-filter-card">
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Search Projects</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="bi bi-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by title or coordinator name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter by Status</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      {getUniqueStatuses().map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="w-100"
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Clear
                  </Button>
                </Col>
              </Row>
              
              {/* Results Summary */}
              {hasActiveFilters && (
                <div className="mt-3 results-summary">
                  <small className="text-muted">
                    Showing {filteredProjects.length} of {projects.length} projects
                    {searchTerm.trim() && ` matching "${searchTerm}"`}
                    {statusFilter !== 'All' && ` with status "${statusFilter}"`}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              {notification.show && (
                <Alert variant={notification.variant} dismissible onClose={dismiss}>
                  {notification.message}
                </Alert>
              )}
              {loading ? (
                <div>Loading...</div>
              ) : filteredProjects.length === 0 ? (
                <div className="text-center py-4 empty-state">
                  <i className="bi bi-inbox display-4 text-muted"></i>
                  <p className="mt-2 text-muted">
                    {hasActiveFilters 
                      ? 'No projects match your current filters. Try adjusting your search criteria.'
                      : 'No projects found.'
                    }
                  </p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Coordinator</th>
                      <th>Status</th>
                      <th>Date Submitted</th>
                      <th style={{ width: 200 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => (
                      <tr key={p.project_id}>
                        <td>{p.title}</td>
                        <td>{p.coordinator_fullname || '—'}</td>
                        <td>
                          <Badge bg={getStatusVariant(p.status)}>{getStatusDisplayText(p.status)}</Badge>
                        </td>
                        <td>{formatDateTime(p.date_submitted)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-view-${p.project_id}`}>View</Tooltip>}>
                              <Button as={Link} to={`/projects/${p.project_id}`} variant="link" className="icon-only-btn icon-view" aria-label="View project">
                                <i className="bi bi-eye"></i>
                              </Button>
                            </OverlayTrigger>
                            
                            {/* Start/End Project buttons removed per request */}

                            {/* Edit Button - using centralized permission system */}
                            {(user && p && canEditItem(user, p, 'project').canEdit) && (
                              <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-edit-${p.project_id}`}>Edit</Tooltip>}>
                                <Button 
                                  onClick={() => handleEditProject(p)} 
                                  variant="link" 
                                  className="icon-only-btn icon-edit" 
                                  aria-label="Edit project"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                              </OverlayTrigger>
                            )}

                            {/* Delete Button - Admin only */}
                            {user && canDeleteItem(user, p).canDelete && (
                              <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-del-${p.project_id}`}>Delete</Tooltip>}>
                                <Button onClick={() => handleDeleteClick(p)} variant="link" className="icon-only-btn icon-delete" aria-label="Delete project">
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Project Edit Modal */}
      <ProjectEditModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        project={projectToEdit}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Delete Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-trash-fill text-danger" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Deletion</h5>
            <p className="text-muted mb-3">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            {projectToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>{projectToDelete.title}</strong>
                <div className="text-muted mt-1">
                  <small>
                    <i className="bi bi-person-fill me-1"></i>
                    Coordinator: {projectToDelete.coordinator_fullname}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-calendar-event me-1"></i>
                    Submitted: {formatDateTime(projectToDelete.date_submitted)}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-tag me-1"></i>
                    Status: <Badge variant={getStatusVariant(projectToDelete.status)}>{projectToDelete.status}</Badge>
                  </small>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDeleteCancel}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <i className="bi bi-trash-fill me-1"></i>
            Delete Project
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Proposal Modal */}
      <ProjectProposalModal
        show={showProposalModal}
        onHide={() => setShowProposalModal(false)}
        onSuccess={() => {
          // Refresh projects list
          const fetchProjects = async () => {
            try {
              const { data } = await apiClient.get('/projects');
              setProjects(data);
            } catch (err) {
              console.error('Failed to load projects', err);
            }
          };
          fetchProjects();
        }}
      />
    </div>
  );
};

export default Projects;


